import { useState, Suspense, useEffect } from "react";
import { Check } from "lucide-react";
import { Await, Link } from "@remix-run/react";
import Container from "~/components/atoms/Container";
import { OhButton } from "~/components/ui/custom-radix-components/button";
import { getCurrentCustomerCount, toProperCase } from "~/lib/helpers/StringHelper";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import AutoPlaces from "../Geosearch/AutoPlaces";
import { geocodeByAddress } from "react-places-autocomplete";
import { getLocationVars } from "~/lib/helpers/googleLocationDataManager";
import EmailService from "~/lib/services/EmailService";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import { useCurrentRegion } from "~/lib/helpers/CurrencyHelper";
import { getConfig, regionConfigs } from "../../../../custom-config/region.configs";
import { ClientOnly } from "../ClientOnly/client-only";
import { getCountryName } from "~/lib/helpers/constants";
import { getItem, removeItem } from "~/utils/storage";
import { submitPardotFormHandler } from "~/lib/utils/PardotUtils/index";
import { QuoteRequestFlowModalFormHandler } from "~/lib/helpers/regionPardotFormHelper";
import ListingsService from "~/lib/services/ListingsService";
import {
    LEAD_FORMS,
    LEAD_STATUS,
    trackFormStep,
    trackLead,
    trackQuoteRequestConversion,
} from "~/lib/ConversionTracking";
import { HappyClientsAvatars } from "../common/HappyClientsAvatars";
import { loadGoogleMapsScript } from "~/utils/loadGoogleMaps";
import { convertSearchUrlToLocationPage } from "~/components/blocks/Geosearch/locationGeosearch";

interface ModernHeroSectionProps {
    countryName: string;
    region: string;
    loaderData: any;
    city?: string;
    onContinue?: () => void;
    isCampaignPage?: boolean;
    step?: number;
    onBack?: () => void;
    isMobile?: boolean;
}

export default function ModernHeroSection({
    countryName,
    region,
    loaderData,
    city,
    onContinue,
    isCampaignPage = false,
    step = 1,
    onBack,
    isMobile = false,
}: ModernHeroSectionProps) {
    const regionConfig = useCurrentRegion();
    const [formData, setFormData] = useState({
        location: city,
        locationLatLng: { lat: undefined as any, lng: undefined as any },
        locationCountry: "",
        locationState: "",
        locationStreet: "",
        locationCity: city,
        geoSearch: "",
        teamSize: "",
        minDesk: 1,
        maxDesk: 1,
        budget: 0,
        budgetCurrency: regionConfigs[region as keyof typeof regionConfigs]?.currency || "USD",
        email: "",
        firstName: "",
        lastName: "",
        company: "",
        phone: "",
        comments: "",
        ppcKeywords: "",
        campaign: "",
        device: "",
        network: "",
        workspace: "",
        userLandingUrl: "",
        formUrl: typeof window !== "undefined" ? window.location.href : ""
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isEmailChecking, setIsEmailChecking] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [phoneCountry, setPhoneCountry] = useState((regionConfig?.region || region) === "uk" ? "gb" : (regionConfig?.region || region));

    useEffect(() => {
        const campaignData = getItem<{
            keyword?: string;
            network?: string;
            device?: string;
            campaign?: string;
            location?: string;
            workspace?: string;
            country?: string;
        }>("campaignData");

        const landingUrl = getItem("landingUrl");

        setFormData(prev => ({
            ...prev,
            ppcKeywords: campaignData?.keyword || "",
            campaign: campaignData?.campaign || "",
            device: campaignData?.device || "",
            network: campaignData?.network || "",
            workspace: campaignData?.workspace || "",
            userLandingUrl: String(landingUrl || ""),
        }));
    }, []);

    const formatBudget = (value: number) =>
        value && !Number.isNaN(value) ? value.toLocaleString() : "";

    const validateEmail = async () => {
        if (!formData.email) {
            setErrors(prev => ({ ...prev, email: "Email is required" }));
            return false;
        }
        setIsEmailChecking(true);
        try {
            const res = EmailService.verifyEmail(encodeURIComponent(formData.email));
            const data = await res;

            if (!data?.verified) {
                setErrors(prev => ({ ...prev, email: "Please enter a valid email address" }));
                return false;
            }
            return true;
        } catch (err) {
            setErrors(prev => ({ ...prev, email: "Could not verify email. Try again." }));
            return false;
        } finally {
            setIsEmailChecking(false);
        }
    };

    const validateStep1 = async () => {
        const newErrors: Record<string, string> = {};
        if (!formData.location) newErrors.location = "Location is required";
        if (!formData.teamSize) newErrors.teamSize = "Team size is required";

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return false;

        const emailOk = await validateEmail();
        return emailOk;
    };

    const validateStep2 = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.firstName) newErrors.firstName = "First name is required";
        if (!formData.lastName) newErrors.lastName = "Last name is required";
        if (!formData.company) newErrors.company = "Company is required";
        if (!formData.phone) newErrors.phone = "Phone is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const sendToPardot = ({ isComplete = false }, callback?: () => void) => {
        const campaignData = getItem<{
            keyword?: string;
            network?: string;
            device?: string;
            campaign?: string;
            location?: string;
            workspace?: string;
            country?: string;
        }>("campaignData");

        const comments = `
      Comments: ${formData.comments}
      Location: ${formData.location}
      | State: ${formData.locationState}
      | Street: ${formData.locationStreet}
      | Lat lng: ${formData.locationLatLng.lat}, ${formData.locationLatLng.lng}
      | Number of desks: ${formData.teamSize}
      | Budget: ${formData.budget || 0} ${formData.budgetCurrency}
    `;

        const pardotPayload: any = {
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            company: formData.company,
            phone: formData.phone,
            minPeople: formData.minDesk,
            maxPeople: formData.maxDesk,
            officeType: formData.teamSize,
            maxBudget: formData.budget || 0,
            location: formData.location || "",
            locationCity: formData.locationCity || "",
            locationCountry: formData.locationCountry || "",
            locationState: formData.locationState || "",
            locationStreet: formData.locationStreet || "",
            comments,
            formUrl: formData.formUrl,
            userLandingUrl: formData.userLandingUrl || String(getItem("landingUrl") || ""),
            country: formData.locationCountry || getCountryName(region),
            ppcKeywords: campaignData?.keyword || formData.ppcKeywords || (formData as any).userKeywords || "",
            campaign: campaignData?.campaign || formData.campaign || "",
            device: campaignData?.device || formData.device || "",
            network: campaignData?.network || formData.network || "",
            workspace: campaignData?.workspace || formData.teamSize || "",
            leadStatus: "New",
        };

        submitPardotFormHandler(
            QuoteRequestFlowModalFormHandler(region, isComplete),
            pardotPayload,
            () => ListingsService.sendErrorNotification({ ...formData }, "quote-request"),
            callback
        );
    };

    const handleSubmitStep1 = async (e: React.FormEvent) => {
        e.preventDefault();
        const isValid = await validateStep1();
        if (isValid) {
            sendToPardot({ isComplete: false }, async () => {
                // Step 1 already creates a Pardot prospect, so it is a partial lead.
                trackLead({
                    form: LEAD_FORMS.CAMPAIGN_HERO,
                    status: LEAD_STATUS.PARTIAL,
                    userData: { email: formData.email, phone_number: formData.phone },
                });
                try {
                    await ListingsService.sendSuccessNotification({ ...formData }, "quote-request");
                } catch {
                    // The lead may still have reached Pardot; nothing to retry here.
                }
            });
            if (onContinue) {
                onContinue();
            }
        }
    };

    const handleBackStep = () => {
        if (onBack) {
            onBack();
        }
    };

    const handleSendRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        if (validateStep2()) {
            // The last step has no step state of its own (the route only counts
            // 1 and 2), so Send Request reports itself. Fired on a valid submit,
            // before the network calls, so a blocked Pardot cannot silence it.
            trackFormStep({ form: LEAD_FORMS.CAMPAIGN_HERO, step: 3 });
            setIsSubmitting(true);
            try {
                // Send success notification immediately (don't wait for Pardot)
                await ListingsService.sendSuccessNotification({ ...formData }, "quote-request");
                trackQuoteRequestConversion();
                removeItem("campaignData");
                removeItem("ppcKeywords");
            } catch (err) {
                // Silent fail or handle error silently
            }

            // Pardot is secondary, we don't want to block the UI for it
            sendToPardot({ isComplete: true }, () => {
                trackLead({
                    form: LEAD_FORMS.CAMPAIGN_HERO,
                    status: LEAD_STATUS.COMPLETE,
                    userData: { email: formData.email, phone_number: formData.phone },
                });
                setIsSubmitting(false);
                setIsSuccess(true);
            });
        }
    };


    const officeSizeOptions = [
        { label: "1-2 desks", minDesk: 1, maxDesk: 2 },
        { label: "3-5 desks", minDesk: 3, maxDesk: 5 },
        { label: "6-10 desks", minDesk: 6, maxDesk: 10 },
        { label: "11-20 desks", minDesk: 11, maxDesk: 20 },
        { label: "21+ desks", minDesk: 21, maxDesk: 100 },
    ];

    return (
        <section className="relative w-full overflow-hidden bg-gray-50 font-montserrat min-h-screen flex items-center py-10 md:py-0">
            {/* Background Image */}
            <div className="absolute inset-0 z-0">
                <img
                    src={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/home.avif`}
                    alt="Modern office background"
                    className="h-full w-full object-cover object-center opacity-40 md:opacity-100"
                    loading="eager"
                    fetchPriority="high"
                />
                <div className="absolute inset-0 bg-white/30 md:bg-transparent" />
            </div>

            <Container htmlFor="fluid" className="relative z-10 w-full pb-4">
                <div className="mx-auto flex max-w-7xl flex-col gap-10 px-6 pt-8 md:flex-row md:items-center">
                    <Link
                        data-spinner="true"
                        to={region === "us" ? "/" : `/${region}`}
                        prefetch="intent"
                        aria-label="Office Hub Home"
                    >
                        <img
                            src={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/office-hub.avif`}
                            alt="Office Hub Logo"
                            title="Office Hub"
                            width={175}
                            height={50}
                            className="w-[170px] h-[48px] object-contain"
                        />
                    </Link>
                </div>
                <div className="mx-auto flex max-w-7xl flex-col gap-10 px-6 md:flex-row md:items-center">


                    {/* Left Content - Static Frame */}
                    <div className="flex flex-1 flex-col items-start gap-8">

                        <div className="flex flex-col gap-[30px]">
                            <h1 className="text-[30px] font-bold leading-tight text-oh-black md:text-3xl lg:text-4xl">
                                Need Office Space in {toProperCase(city) || countryName}?<br />
                                Don't Wait Weeks
                            </h1>
                            <p className="max-w-lg text-lg text-gray-700 font-semibold">
                                Skip endless searching. Tell us your requirements and get office options sent to you today.
                            </p>
                        </div>

                        {/* Checklist */}
                        <div className="flex flex-col gap-2.5">
                            {[
                                `Compare ${toProperCase(city || countryName)} offices with one enquiry`,
                                "Get shortlisted options within 24 hours",
                                "Flexible terms and fast approvals",
                                "Free local experts",
                            ].map((item, index) => (
                                <div key={index} className="flex items-center gap-[30px]">
                                    <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#11AFB8]">
                                        <Check className="h-3 w-3 text-white" />
                                    </div>
                                    <span className="text-sm font-medium text-oh-black">{item}</span>
                                </div>
                            ))}
                        </div>

                        {/* Social Proof */}
                        <div
                            className="cursor-pointer w-full"
                            onClick={() => {
                                const element = document.getElementById("reviews");
                                if (element) {
                                    element.scrollIntoView({ behavior: "smooth" });
                                }
                            }}
                        >
                            <div className="mt-2 flex flex-row items-center gap-4 sm:flex-row sm:items-center">
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <HappyClientsAvatars className="flex -space-x-3" imageClassName="h-8 w-8" />
                                    <span className="text-xs font-semibold text-gray-900">
                                        {getCurrentCustomerCount()}+ Happy Clients
                                    </span>
                                </div>

                                <Suspense fallback={null}>
                                    <Await resolve={loaderData.homeSummary}>
                                        {(homeSummary) => (
                                            <div className="flex items-center bg-gray-100 px-2 py-1 rounded-md text-base font-semibold">
                                                <img
                                                    src={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/google-fav-logo.avif`}
                                                    alt="Google Logo"
                                                    className="w-6 h-6 mr-2"
                                                />
                                                ⭐ {parseFloat(homeSummary?.sumRatings) > 0 ? parseFloat(homeSummary.sumRatings).toFixed(1) : '5.0'}
                                            </div>
                                        )}
                                    </Await>
                                </Suspense>
                            </div>
                        </div>
                    </div>

                    {/* Right Content - Form Card */}
                    <div className="w-full max-w-[440px] shrink-0 min-h-[550px] flex items-start transition-all duration-300">
                        <div className="relative overflow-hidden rounded-xl bg-white shadow-xl w-full min-h-[550px] flex flex-col">
                            {/* Dynamic Progress Bar */}
                            <div className="absolute top-0 left-0 right-0 h-1 bg-gray-100 overflow-hidden">
                                <div
                                    className="h-full bg-[#11AFB8] transition-all duration-500 ease-in-out"
                                    style={{ width: step === 1 ? '50%' : '100%' }}
                                />
                            </div>

                            <div className="p-6 flex-1 flex flex-col pt-8">
                                {step === 1 ? (
                                    <div className="animate-in fade-in slide-in-from-right-4 duration-500 flex flex-col h-full overflow-visible">
                                        <h2 className="mb-6 text-2xl font-bold text-oh-black">
                                            Find your perfect office<br />space today
                                        </h2>

                                        <form className="flex flex-col gap-4" onSubmit={handleSubmitStep1}>
                                            <div className="flex flex-col gap-1.5 relative overflow-visible">
                                                <label className="text-xs font-semibold text-gray-600">Where do you need Office Space?</label>
                                                <ClientOnly fallback={<div className="h-11 w-full animate-pulse bg-gray-50 border border-gray-200 rounded-lg" />}>
                                                    {() => (
                                                        <AutoPlaces
                                                            value={formData.location}
                                                            mobile={isMobile}
                                                            inModal={true}
                                                            inputStyleClassName={`h-11 bg-white border-gray-200 focus:ring-1 focus:ring-gray-300 rounded-lg text-sm text-oh-black ${errors.location ? 'border-red-500 bg-red-50' : ''}`}
                                                            onLocationSelect={async (val: string, location: string) => {
                                                                if (!val || !location) {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        location: "",
                                                                        geoSearch: "",
                                                                        locationCountry: "",
                                                                        locationState: "",
                                                                        locationStreet: "",
                                                                        locationCity: "",
                                                                        locationLatLng: { lat: undefined, lng: undefined }
                                                                    }));
                                                                    return;
                                                                }

                                                                const data: any = { geoSearch: "", locationCountry: "", locationState: "", locationStreet: "", locationCity: "" };
                                                                const [base, query] = val.split("?");
                                                                data.geoSearch = convertSearchUrlToLocationPage(val);

                                                                const segments = base.split("/").filter(Boolean);
                                                                const regionCode = segments[0] === "search" ? "us" : segments[0];
                                                                const locationSegments =
                                                                    segments[0] === "search" ? segments.slice(1) : segments.slice(1);

                                                                // Location URLs no longer carry lat/lng in the query string.
                                                                const partsQ = new URLSearchParams(query || "");
                                                                let lat: any = partsQ.get("latitude") || undefined;
                                                                let lng: any = partsQ.get("longitude") || undefined;

                                                                const config = getConfig(regionCode as any) as any;
                                                                data.locationCountry = config?.country || "";
                                                                if (
                                                                    locationSegments.length >= 2 &&
                                                                    !locationSegments[0]?.startsWith("office-space")
                                                                ) {
                                                                    data.locationCity = locationSegments[locationSegments.length - 1];
                                                                    data.locationState = locationSegments[0] || "";
                                                                }

                                                                try {
                                                                    // This page does not preload Google Maps JS. geocodeByAddress
                                                                    // throws if maps is missing; load it first and still save the
                                                                    // selected location if geocoding cannot run.
                                                                    const mapsReady = await new Promise<boolean>((resolve) =>
                                                                        loadGoogleMapsScript(resolve),
                                                                    );
                                                                    if (mapsReady && (window as any).google?.maps) {
                                                                        const geocodeResults = await geocodeByAddress(location);
                                                                        const { state_long, state_short, streetName, city, country, country_short } = getLocationVars(geocodeResults as any);

                                                                        data.locationState = state_long || state_short || data.locationState;
                                                                        data.locationStreet = streetName || "";
                                                                        if (city) data.locationCity = city;
                                                                        if (country) data.locationCountry = country;

                                                                        const point = (geocodeResults as any)?.[0]?.geometry?.location;
                                                                        if ((lat == null || lng == null) && point) {
                                                                            lat = typeof point.lat === "function" ? point.lat() : point.lat;
                                                                            lng = typeof point.lng === "function" ? point.lng() : point.lng;
                                                                        }

                                                                        const matchingConfig = Object.entries(regionConfigs).find(
                                                                            ([key, cfg]) => {
                                                                                const normalizedLocationCountry = (country || "").replace(/-/g, " ");
                                                                                return (
                                                                                    cfg.country.toLowerCase() === normalizedLocationCountry.toLowerCase() ||
                                                                                    key.toLowerCase() === country_short?.toLowerCase() ||
                                                                                    (key.toLowerCase() === "uk" && country_short?.toLowerCase() === "gb")
                                                                                );
                                                                            }
                                                                        );

                                                                        if (matchingConfig) {
                                                                            const configKey = matchingConfig[0];
                                                                            setPhoneCountry(configKey === "uk" ? "gb" : configKey);
                                                                            data.budgetCurrency = (matchingConfig[1] as any).currency;
                                                                        }
                                                                    } else if (config?.currency) {
                                                                        data.budgetCurrency = config.currency;
                                                                        if (regionCode) {
                                                                            setPhoneCountry(regionCode === "uk" ? "gb" : regionCode);
                                                                        }
                                                                    }
                                                                } catch (err) {
                                                                    console.error("Error resolving location details", err);
                                                                    if (config?.currency) {
                                                                        data.budgetCurrency = config.currency;
                                                                    }
                                                                }

                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    ...data,
                                                                    location,
                                                                    locationLatLng: { lat, lng }
                                                                }));

                                                                if (errors.location) setErrors(prev => ({ ...prev, location: "" }));
                                                            }}
                                                        />
                                                    )}
                                                </ClientOnly>
                                                {errors.location && <span className="text-[10px] text-red-500 font-medium">{errors.location}</span>}
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <label className="text-xs font-semibold text-gray-600">Number of desks</label>
                                                <Select
                                                    value={formData.teamSize}
                                                    onValueChange={(val) => {
                                                        const chosen = officeSizeOptions.find(o => o.label === val);
                                                        setFormData({
                                                            ...formData,
                                                            teamSize: val,
                                                            minDesk: chosen?.minDesk || 1,
                                                            maxDesk: chosen?.maxDesk || 1
                                                        });
                                                        if (errors.teamSize) setErrors({ ...errors, teamSize: "" });
                                                    }}
                                                >
                                                    <SelectTrigger
                                                        className={`w-full bg-white border h-11 rounded-lg text-sm transition focus:ring-1 focus:ring-gray-300 ${errors.teamSize ? 'border-red-500 text-red-900 bg-red-50' : 'border-gray-200 text-oh-black'}`}
                                                    >
                                                        <SelectValue className="text-oh-black" placeholder="Select team size" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-white border rounded-lg shadow-lg z-[100]">
                                                        {officeSizeOptions.map(opt => (
                                                            <SelectItem key={opt.label} value={opt.label} className="cursor-pointer focus:bg-gray-100 focus:text-oh-black">
                                                                {opt.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {errors.teamSize && <span className="text-[10px] text-red-500 font-medium">{errors.teamSize}</span>}
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <label className="text-xs font-semibold text-gray-600">Monthly budget</label>
                                                <div className={`flex overflow-hidden rounded-lg border transition ${errors.budget ? 'border-red-500' : 'border-gray-200 focus-within:border-gray-300 focus-within:ring-1 focus-within:ring-gray-100'}`}>
                                                    <Select
                                                        value={formData.budgetCurrency}
                                                        onValueChange={(val) => setFormData(prev => ({ ...prev, budgetCurrency: val }))}
                                                    >
                                                        <SelectTrigger className="w-24 rounded-tr-none rounded-br-none bg-white border-0 border-r border-gray-200 focus:ring-transparent h-11 text-oh-black">
                                                            <SelectValue className="text-oh-black" placeholder={formData.budgetCurrency} />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-white border rounded-lg shadow-lg z-[100]">
                                                            {Array.from(new Set(Object.values(regionConfigs).map((cfg: any) => cfg.currency)))
                                                                .sort()
                                                                .map((currency) => (
                                                                    <SelectItem key={currency} value={currency as string} className="cursor-pointer focus:bg-gray-100 focus:text-oh-black">
                                                                        {currency as string}
                                                                    </SelectItem>
                                                                ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <input
                                                        type="text"
                                                        className="w-full px-3 py-2.5 text-sm outline-none bg-white text-oh-black"
                                                        placeholder="Please enter your monthly budget"
                                                        value={formatBudget(formData.budget)}
                                                        onChange={(e) => {
                                                            const raw = e.target.value.replace(/,/g, "");
                                                            const num = Number(raw);
                                                            if (!isNaN(num)) {
                                                                setFormData(prev => ({ ...prev, budget: num }));
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <label className="text-xs font-semibold text-gray-600">Email</label>
                                                <input
                                                    type="email"
                                                    className={`w-full rounded-lg border p-2.5 text-sm outline-none bg-white text-oh-black transition ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-200 focus:border-[#11AFB8] focus:ring-1 focus:ring-[#11AFB8]'}`}
                                                    placeholder="Type your work email"
                                                    value={formData.email}
                                                    onChange={(e) => {
                                                        setFormData({ ...formData, email: e.target.value });
                                                        if (errors.email) setErrors({ ...errors, email: "" });
                                                    }}
                                                />
                                                {errors.email && <span className="text-[10px] text-red-500 font-medium">{errors.email}</span>}
                                            </div>

                                            <OhButton type="submit" size="sm" className="mt-4 w-full bg-[#11AFB8] py-5 text-sm font-bold uppercase tracking-wide hover:bg-[#0e8f96]" disabled={isEmailChecking}>
                                                {isEmailChecking ? "Verifying..." : "Continue"}
                                            </OhButton>
                                        </form>
                                    </div>
                                ) : (
                                    <div className="animate-in fade-in slide-in-from-right-4 duration-500 flex flex-col h-full">
                                        <form className="flex flex-col gap-4 flex-1" onSubmit={handleSendRequest}>
                                            <div className="grid grid-cols-2 gap-[30px]">
                                                <div className="space-y-1.5">
                                                    <Label htmlFor="firstName" className="text-xs font-semibold text-gray-700">First Name</Label>
                                                    <Input
                                                        id="firstName"
                                                        placeholder="First name"
                                                        className={`h-11 bg-white text-oh-black ${errors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                                                        value={formData.firstName}
                                                        onChange={(e) => {
                                                            setFormData({ ...formData, firstName: e.target.value });
                                                            if (errors.firstName) setErrors({ ...errors, firstName: "" });
                                                        }}
                                                    />
                                                    {errors.firstName && <span className="text-[9px] text-red-500">{errors.firstName}</span>}
                                                </div>
                                                <div className="space-y-1.5">
                                                    <Label htmlFor="lastName" className="text-xs font-semibold text-gray-700">Last Name</Label>
                                                    <Input
                                                        id="lastName"
                                                        placeholder="Last name"
                                                        className={`h-11 bg-white text-oh-black ${errors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                                                        value={formData.lastName}
                                                        onChange={(e) => {
                                                            setFormData({ ...formData, lastName: e.target.value });
                                                            if (errors.lastName) setErrors({ ...errors, lastName: "" });
                                                        }}
                                                    />
                                                    {errors.lastName && <span className="text-[9px] text-red-500">{errors.lastName}</span>}
                                                </div>
                                            </div>

                                            <div className="space-y-1.5">
                                                <Label htmlFor="company" className="text-xs font-semibold text-gray-700">Company</Label>
                                                <Input
                                                    id="company"
                                                    placeholder="Enter your company name"
                                                    className={`h-11 bg-white text-oh-black ${errors.company ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                                                    value={formData.company}
                                                    onChange={(e) => {
                                                        setFormData({ ...formData, company: e.target.value });
                                                        if (errors.company) setErrors({ ...errors, company: "" });
                                                    }}
                                                />
                                                {errors.company && <span className="text-[9px] text-red-500">{errors.company}</span>}
                                            </div>

                                            <div className="space-y-1.5">
                                                <Label htmlFor="phone" className="text-xs font-semibold text-gray-700">Phone</Label>
                                                <PhoneInput
                                                    country={phoneCountry as any}
                                                    value={formData.phone}
                                                    onChange={(phone) => {
                                                        setFormData({ ...formData, phone });
                                                        if (errors.phone) setErrors({ ...errors, phone: "" });
                                                    }}
                                                    inputStyle={{ width: "100%", paddingLeft: 60, borderColor: errors.phone ? "#ef4444" : "#e6e6e6", height: '44px', borderRadius: '8px', backgroundColor: 'white', color: '#1a181e' }}
                                                    buttonStyle={{ width: 50, borderColor: errors.phone ? "#ef4444" : "#e6e6e6", borderRadius: '8px 0 0 8px', backgroundColor: 'white' }}
                                                />
                                                {errors.phone && <span className="text-[9px] text-red-500">{errors.phone}</span>}
                                            </div>

                                            <div className="space-y-1.5">
                                                <Label htmlFor="comments" className="text-xs font-semibold text-gray-700">Anything else? (Optional)</Label>
                                                <Textarea
                                                    id="comments"
                                                    placeholder="Specific requirements..."
                                                    className="min-h-[80px] border-gray-200 text-sm bg-white text-oh-black"
                                                    value={formData.comments}
                                                    onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                                                />
                                            </div>

                                            <div className="flex gap-[30px] mt-auto pt-4">
                                                <OhButton
                                                    type="button"
                                                    variant="secondary"
                                                    className="flex-1 py-5 text-sm font-bold uppercase tracking-wide bg-gray-100 text-gray-900 border-none hover:bg-gray-200"
                                                    onClick={handleBackStep}
                                                >
                                                    Back
                                                </OhButton>
                                                <OhButton
                                                    type="submit"
                                                    className="flex-[2] py-5 text-sm font-bold uppercase tracking-wide bg-[#11AFB8] hover:bg-[#0e8f96]"
                                                    disabled={isSubmitting}
                                                >
                                                    {isSubmitting ? "Sending..." : "Send Request"}
                                                </OhButton>
                                            </div>
                                        </form>
                                    </div>
                                )}
                                {isSuccess && (
                                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 absolute inset-0 z-20 flex flex-col items-center justify-center bg-white p-6 text-center">
                                        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                                            <Check className="h-8 w-8 text-green-600" />
                                        </div>
                                        <h3 className="mb-2 text-2xl font-bold text-oh-black">Request Sent!</h3>
                                        <p className="text-gray-600">
                                            Thank you for your interest. One of our local experts will reach out to you shortly with tailored options.
                                        </p>
                                        <OhButton
                                            className="mt-8 bg-[#11AFB8] hover:bg-[#0e8f96]"
                                            onClick={() => {
                                                setIsSuccess(false);
                                                if (onBack) onBack();
                                            }}
                                        >
                                            Done
                                        </OhButton>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            </Container>
        </section >
    );
}
